import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { msg } from 'gt-react';
import { SD_849_REVIEW_SURFACE_ACCESS } from '@erp/authz';
import { ProductModule } from '@erp/shared-types';
import { ListChecksIcon } from '@phosphor-icons/react';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requirePermission,
} from '../../route';
import { reviewSearchSchema } from './reviewView';

const ReviewPage = lazyRouteComponent(
  () => import('./ReviewPage'),
  'ReviewPage',
);

/**
 * The 849 review page (spec #7634, #7656): one surface where the SD team opens an
 * email and answers everything about it. It replaced the labeling page's Review
 * and Matches modes and the 849 inbox.
 *
 * Guarded by `SD_849_REVIEW_SURFACE_ACCESS` — the record's Read plus the one
 * surface permission, the same list every handler on the surface checks
 * (`assertCanUseReviewSurface`), so the page and its requests cannot drift
 * apart. Update does not imply Read in CASL, and the page opens emails through
 * the record's Read, so it requires ALL of them.
 */
export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/849-review',
  staticData: {
    app: {
      nav: {
        label: msg('849 Review', {
          context: 'sidebar nav label, Ship and Debit section',
        }),
        icon: <ListChecksIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        order: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAllOf: [...SD_849_REVIEW_SURFACE_ACCESS],
      },
    },
  },
  component: ReviewPage,
  loader: () => ({
    crumb: msg('849 Review', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await Promise.all(
      SD_849_REVIEW_SURFACE_ACCESS.map((permission) =>
        requirePermission({
          context,
          action: permission.action,
          subject: permission.subject,
        }),
      ),
    );
  },
  validateSearch: reviewSearchSchema,
});
